import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Loading, LoadingError } from '../components';
import { APIError } from './APIClient';
import { LoadingState } from './LoadingState';

interface SuccessResult<T> {
  data: T;
  success: true;
  error: undefined;
  loading: false;
  refresh: () => void;
}

interface ErrorResult {
  data: undefined;
  success: false;
  error: Error;
  loading: false;
  refresh: () => void;
}

interface LoadingResult {
  data: undefined;
  success: false;
  error: undefined;
  loading: true;
  refresh: () => void;
}

/**
 * The result of a fetcher operation, which can be in one of three states: loading, error, or success.
 * See {@link ErrorResult}, {@link LoadingResult}, and {@link SuccessResult} for more information.
 */
type FetcherResult<T> = ErrorResult | LoadingResult | SuccessResult<T>;

/**
 * The properties for the {@link Fetcher} component.
 *
 * @param {Object} result The result of the fetcher operation.
 * @param {boolean} [inline] Whether to render the loading state inline or as a full page loader. (false by default)
 * @param {Object} [children] The children to render when the fetcher operation is successful.
 */
interface FetcherProps<T> {
  readonly result: FetcherResult<T>;
  readonly inline?: boolean;
  readonly children?: ReactElement;
}

/**
 * A component that generically handles the loading and error states of a {@link useFetcher} operation.
 *
 * This method can also handle permissions errors, and can have children that are rendered when the
 * fetcher operation is successful.
 *
 * @param param0 a {@link FetcherProps} object that contains at least the `result` property.
 * @returns A ReactElement that represents the loading state, error state, or the children of the component.
 *
 * Optionally, the `inline` property can be set to `true` to render the loading state inline instead of
 * as a full page loader.
 */
export const Fetcher = <T,>({ result, inline, children }: FetcherProps<T>): ReactElement | null => {
  if (result.loading) {
    return <Loading inline={inline} />;
  }

  if (result.error) {
    const { error } = result;
    let errorMessage = 'Failed to load';

    if (error instanceof APIError) {
      const { errors } = error.apiResponse;
      if (errors.resource) {
        errorMessage = `${errors.resource.name} not found`;
      }

      if (errors.access) {
        errorMessage = `Missing permissions: ${errors.access.permissions?.join(', ') ?? errors.access.message}`;
      }
    }

    return <LoadingError errorText={errorMessage} retryCallback={result.refresh} />;
  }

  if (!children) {
    return null;
  }

  return children;
};

/**
 * A hook that handles fetching data from an API and provides a loading state and error state
 * handling.
 *
 * For generic loading / error handling, including permissions errors, use the {@link Fetcher} component when `fetcher.success` is `false`.
 *
 * @param effect The effect to run to fetch the data
 * @param deps The dependencies to watch for changes
 * @returns A {@link FetcherResult<T>} object that contains the data, loading state, error state, and
 * a refresh function.
 */
export function useFetcher<T>(
  effect: () => Promise<T>,
  deps: readonly unknown[] = [],
): FetcherResult<T> {
  const [refreshToken, setRefreshToken] = useState(0);
  const [data, setData] = useState<T | undefined>(undefined);
  const [state, setState] = useState<LoadingState>(LoadingState.Loading);
  const [error, setError] = useState<Error>();

  const refresh = useCallback((): void => {
    setRefreshToken((rt) => rt + 1);
  }, []);

  useEffect(() => {
    let ignore = false;

    async function fetchData(): Promise<void> {
      setState(LoadingState.Loading);
      setError(undefined);

      try {
        const result = await effect();
        if (!ignore) {
          setData(result);
          setState(LoadingState.Done);
        }
      } catch (error_) {
        if (!ignore) {
          setError(error_ as Error);
          setData(undefined);
          setState(LoadingState.Error);
        }
      }
    }

    // Let the callback fire asynchronously
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchData();
    // The callback is used a destructor - this avoids race conditions
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, refreshToken]);

  return useMemo(() => {
    return {
      success: state === LoadingState.Done,
      data: data as T,
      loading: state === LoadingState.Loading,
      error,
      refresh,
    } as FetcherResult<T>;
  }, [state, data, error, refresh]);
}
